/* BlairTLV — video governor (Claude 2026-08-03)
 *
 * THE BUG THIS REPLACES
 * ---------------------
 * sections/slideshow.liquid used to load ALL of a slideshow's slide videos the moment the section
 * came within 600px, and call play() on every one of them. Nothing ever paused them again. Measured
 * on the homepage at an iPhone viewport: 17 <video> elements, all with src attached and ALL 17
 * un-paused at the same time, every one an HD-1080p file (2.5-7.2 Mbps) painted into a 390x250 box
 * — ~114MB of video and 17 simultaneous H.264 decode pipelines. iOS only gives a page a handful of
 * hardware decoders, so past that limit playback visibly stutters and then simply STOPS. That is
 * exactly the "the video is lagging and stopping" the founder reported.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 * One governor for the whole page decides, on every scroll/slide change, which videos are worth
 * holding a decoder for. The founder's requirement (2026-08-03) was "load what's necessary, but in
 * a way that my swipe mechanism feels smooth" — so it is not enough to only run the visible video;
 * whatever he is ABOUT to swipe onto has to be decoded and waiting, or he sees a poster pop.
 *
 *   PLAY    the one slide he is looking at                    (always exactly 1)
 *   WARM    EVERY other slide of the section on screen, plus the
 *           first slide of the panels directly above/below — src
 *           attached and the first frame decoded, but PAUSED, so
 *           each costs a buffer rather than a decoder
 *   RELEASE everything else — pause, strip src, load(). That frees
 *           the decoder AND aborts the in-flight download; the
 *           poster (the product photo) stays on screen, so a
 *           released slide looks identical to a loaded one.
 *
 * The budget is per-SECTION rather than a flat count (founder, 2026-08-04: "load 5 video so the
 * slideshow wont need to mount and unmount the video each time"). An earlier flat 1-playing-plus-3-
 * warm budget could not hold a 5-slide slideshow, so its far slide was released and re-attached on
 * every auto-rotate — re-downloading and re-decoding the same clip while he sat watching it.
 *
 * Net: exactly 1 playing, versus the 17 this replaced. Full 1080p everywhere — the founder chose
 * maximum quality (2026-08-03: "high quality is very important to me in the videos"), and quality
 * was never the problem; concurrency was.
 *
 * NO FORCED LAYOUT. Ranking uses intersection ratios handed to us by IntersectionObserver, never
 * getBoundingClientRect(). The homepage carries ~10,000 DOM nodes, so a single stray measurement on
 * the scroll path costs hundreds of ms (see the tuneCV note in blair-swipe-deck-v4.js).
 *
 * Warming on iOS: Safari largely ignores preload="auto" until something plays. So a warm video is
 * primed with play()-then-pause(): that decodes the first frame and fills the buffer, then hands
 * the decoder straight back. Muted + playsinline, so it never makes noise and never goes fullscreen.
 *
 * Markup contract (sections/slideshow.liquid): <video class="slideshow__video" data-blair-lazy
 * muted loop playsinline preload="none" poster=...> with <source data-src="..."> and no src.
 */
(function () {
  'use strict';

  // Blair (founder, 2026-08-04): "load 5 video so the slideshow wont need to mount and unmount the
  // video each time". He is right, and it was a real flaw in the first version: with a budget of
  // 1 playing + 3 warm, a 5-slide slideshow could not hold all of its own slides, so as it
  // auto-rotated the far slide was released and re-attached every cycle — re-downloading and
  // re-decoding the same clip over and over, for a section he is sitting on and watching.
  //
  // The rule is now per-SECTION, not a flat count: whichever slideshow is on screen keeps EVERY one
  // of its slides attached, so rotating between them mounts nothing. Only ONE of them ever plays; the
  // rest sit paused with their first frame decoded. Leaving the section still releases the lot.
  //
  // This does not reintroduce the original fault. That was 17 videos PLAYING at once across four
  // slideshows, which is what exhausts iOS's decoders. Here the ceiling is one section's slides
  // (5 today) plus one primed slide for each adjacent section — and exactly one of them is ever
  // un-paused.
  var NEIGHBOUR_WARM = 2;
  var DESKTOP = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: false };    // adjacent sections get their first slide primed, nothing more
  var SEC_NEAR = '60% 0px';  // a panel this close counts as "he might swipe onto it next"

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    if (!('IntersectionObserver' in window)) return;   // ancient browser: leave the posters showing

    var sections = [];   // { el, ratio, near, slides:[{el, vid, ratio}] }

    [].slice.call(document.querySelectorAll('.slideshow')).forEach(function (slider) {
      var root = slider.closest('.shopify-section') || slider.parentNode;
      var slides = [].slice.call(slider.querySelectorAll('.slideshow__slide'))
        .map(function (s) { return { el: s, vid: s.querySelector('video.slideshow__video[data-blair-lazy]'), ratio: 0 }; })
        .filter(function (s) { return !!s.vid; });
      if (!slides.length) return;
      sections.push({ el: root, slider: slider, ratio: 0, near: false, slides: slides });
    });

    if (!sections.length) return;

    /* ---------- attach / detach ---------- */

    function attach(v) {
      if (v.dataset.blairLoaded) return;
      v.dataset.blairLoaded = '1';
      var srcs = v.querySelectorAll('source[data-src]');
      for (var i = 0; i < srcs.length; i++) srcs[i].setAttribute('src', srcs[i].getAttribute('data-src'));
      v.preload = 'auto';
      v.muted = true;                                  // re-assert: a muted video is always allowed to autoplay
      v.load();
    }

    // Strip the source back off. `load()` after removing src is what actually releases the decoder
    // and cancels the download — pause() alone leaves both alive. The poster reappears, unchanged.
    function release(v) {
      if (!v.dataset.blairLoaded) return;
      delete v.dataset.blairLoaded;
      delete v.dataset.blairWarm;
      try { v.pause(); } catch (e) {}
      var srcs = v.querySelectorAll('source[src]');
      for (var i = 0; i < srcs.length; i++) srcs[i].removeAttribute('src');
      v.removeAttribute('src');
      v.preload = 'none';
      try { v.load(); } catch (e) {}
    }

    function play(v) {
      attach(v);
      v.dataset.blairPlayed = '1';
      delete v.dataset.blairWarm;
      if (!v.paused) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    // Decode the first frame and fill some buffer, then give the decoder straight back. On iOS this
    // play()/pause() dance is the only reliable way to pre-roll — preload="auto" is advisory there.
    //
    // The pause is armed THREE ways on purpose, because the whole point of the governor is that only
    // one video is ever really playing: `loadeddata` (fires the moment the first frame exists, the
    // normal path), the play() promise, and a hard timeout. If a warm video's play() promise never
    // settles — which happens whenever the decoder is unavailable — relying on it alone would quietly
    // leave four videos running and put us back where we started.
    function warm(v) {
      attach(v);
      if (v.dataset.blairWarm) return;                 // already primed; don't re-prime every scroll frame
      v.dataset.blairWarm = '1';

      function settle() {
        if (v.dataset.blairWarm !== '1') return;        // promoted to the playing slide meanwhile — leave it
        try { v.pause(); if (!v.dataset.blairPlayed) v.currentTime = 0; } catch (e) {}   // never rewind a clip she has already watched
      }

      if (v.readyState >= 3) { settle(); return; }
      v.addEventListener('loadeddata', settle, { once: true });   // first frame decoded — the normal arm
      // The backstop is deliberately short. A verification pass on hardware with NO H.264 decoder —
      // where `loadeddata` never fires at all, so ONLY this timer pauses — measured the priming window
      // at up to 1.48s, and because a warm slide is re-picked on every auto-rotate tick the page spent
      // 41% of a parked minute with three videos un-paused rather than one. Still nowhere near the 17
      // this replaced, but the ceiling should be tight in the worst case too, not just the normal one.
      // On a real device `loadeddata` fires first and this timer never runs.
      setTimeout(settle, 700);
      var p = v.play();
      if (p && p.then) p.then(settle).catch(function () {});
    }

    /* ---------- the decision ---------- */

    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; apply(); });
    }

    function apply() {
      // Tab in the background: hold nothing at all.
      if (document.hidden) {
        sections.forEach(function (s) { s.slides.forEach(function (sl) { release(sl.vid); }); });
        return;
      }

      var wanted = [];   // [{v, mode}] in priority order — first entry plays, the rest warm

      // The panel he is actually looking at.
      var active = null;
      sections.forEach(function (s) { if (s.ratio > 0.05 && (!active || s.ratio > active.ratio)) active = s; });

      if (active) {
        var ai = 0, best = -1;
        active.slides.forEach(function (sl, i) { if (sl.ratio > best) { best = sl.ratio; ai = i; } });
        wanted.push({ v: active.slides[ai].vid, mode: 'play' });
        // Blair (2026-10-18, founder: "make sure the video autoplays"). DESKTOP shows 2.5 cards at once,
        // and only one of them moving read as broken. Desktop browsers are not short of decoders the way
        // iOS is, so there every card of the section on screen that is at least a quarter visible plays.
        // Phones keep exactly one.
        if (DESKTOP.matches) {
          // Any card even slightly on screen keeps playing (founder 2026-10-18: "all video in view play,
          // don't stop or reset them when a step is made"), so a card sliding toward the edge never stalls.
          active.slides.forEach(function (sl, i) { if (i !== ai && sl.ratio > 0) wanted.push({ v: sl.vid, mode: 'play' }); });
        }
        // EVERY other slide of the section he is on, nearest-first so the ones he reaches soonest are
        // primed soonest. Nothing here is released while the section stays on screen, so rotating or
        // swiping between these slides causes no mount/unmount churn at all.
        var n = active.slides.length;
        for (var d = 1; d <= n; d++) {
          var fwd = (ai + d) % n;
          var back = ((ai - d) % n + n) % n;
          if (fwd !== ai) wanted.push({ v: active.slides[fwd].vid, mode: 'warm' });
          if (back !== ai && back !== fwd) wanted.push({ v: active.slides[back].vid, mode: 'warm' });
        }
      }

      // The panels one vertical swipe away: prime their FIRST slide so the panel starts on video,
      // not on a poster that pops a moment later.
      sections.forEach(function (s) {
        if (s === active || !s.near) return;
        wanted.push({ v: s.slides[0].vid, mode: 'warm' });
      });

      // Budget = the whole active section + one primed slide per adjacent section. Everything not on
      // the list goes back to its poster, which is what stops attachments accumulating down the page.
      var budget = (active ? active.slides.length : 1) + NEIGHBOUR_WARM;
      var keep = [], keepSet = [];
      wanted.forEach(function (w) {
        if (keepSet.indexOf(w.v) !== -1 || keep.length >= budget) return;   // de-dupe and cap
        keep.push(w); keepSet.push(w.v);
      });

      sections.forEach(function (s) {
        s.slides.forEach(function (sl) {
          if (keepSet.indexOf(sl.vid) === -1) release(sl.vid);
        });
      });
      keep.forEach(function (w) { (w.mode === 'play' ? play : warm)(w.v); });
    }

    /* ---------- observers (ratios only — never a layout read) ---------- */

    var STEPS = [0, 0.1, 0.25, 0.5, 0.75, 1];

    var ioSection = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = e.target.__blairSec;
        if (s) s.ratio = e.isIntersecting ? e.intersectionRatio : 0;
      });
      schedule();
    }, { threshold: STEPS });

    var ioNear = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = e.target.__blairSec;
        if (s) s.near = e.isIntersecting;
      });
      schedule();
    }, { rootMargin: SEC_NEAR });

    sections.forEach(function (s) {
      s.el.__blairSec = s;
      ioSection.observe(s.el);
      ioNear.observe(s.el);

      // Which slide is on screen, measured against the horizontal slider itself — this reads the
      // same whether the slide changed because he swiped or because auto-rotate scrolled it, and it
      // is direction-agnostic, which matters on this RTL store.
      var ioSlide = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var sl = e.target.__blairSlide;
          if (sl) sl.ratio = e.isIntersecting ? e.intersectionRatio : 0;
        });
        schedule();
      }, { root: s.slider, threshold: STEPS });

      s.slides.forEach(function (sl) { sl.el.__blairSlide = sl; ioSlide.observe(sl.el); });
    });

    document.addEventListener('visibilitychange', schedule);
    schedule();
  });
})();
